import React, { useState, useCallback } from 'react';
import { ScraperOverview, ScraperRun, ScraperError } from '../types/scraper';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// Icons
const IconSpinner = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
    <circle cx="12" cy="12" r="10" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </svg>
);

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const SectionHeader = ({ children, sub }: { children: React.ReactNode; sub?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ marginBottom: 4 }}>
    <h2 style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: T.textLight, marginTop: 6, lineHeight: 1.5 }}>{sub}</p>}
  </div>
  );
};

interface ScraperHistoryProps {
  scraperLoading: boolean;
  scraperOverview: ScraperOverview[];
  scraperRuns: ScraperRun[];
  scraperErrors: ScraperError[];
}

export default function ScraperHistory({ scraperLoading, scraperOverview, scraperRuns, scraperErrors }: ScraperHistoryProps) {
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async (format: 'pdf' | 'csv') => {
    const setExporting = format === 'pdf' ? setExportingPDF : setExportingCSV;
    setExporting(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/shortages/export?format=${format}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(errData.detail || `Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `shortage-report-${timestamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Export failed';
      setExportError(msg);
    } finally {
      setExporting(false);
    }
  }, []);

  const exportBtnStyle = (disabled: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    borderRadius: T.radiusSm,
    border: `1px solid ${T.gold}`,
    background: disabled ? T.offWhite : T.white,
    color: disabled ? T.textLight : T.navy,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
    transition: "all 0.15s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <SectionHeader sub="Track scraper runs, errors, and throughput over time">Shortages &amp; Scraper History</SectionHeader>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => handleExport('csv')}
            disabled={exportingCSV}
            style={exportBtnStyle(exportingCSV)}
            title="Export all active shortages as CSV"
          >
            <IconDownload />
            {exportingCSV ? 'Exporting...' : 'Export CSV'}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exportingPDF}
            style={exportBtnStyle(exportingPDF)}
            title="Export shortage report as PDF with summary, risk radar, cause analysis, and alternatives"
          >
            <IconDownload />
            {exportingPDF ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>
      {exportError && (
        <div style={{
          padding: "10px 14px",
          borderRadius: T.radiusSm,
          background: T.dangerBg,
          border: `1px solid ${T.danger}`,
          color: T.danger,
          fontSize: 13,
        }}>
          Export error: {exportError}
        </div>
      )}
      {scraperLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IconSpinner /></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16 }}>
            {(scraperOverview.length ? scraperOverview : [{
              source: "No data",
              total_runs: 0,
              successful_runs: 0,
              failed_runs: 0,
              total_fetched: 0,
              total_inserted: 0,
              total_errors: 0,
              avg_duration_seconds: null,
              last_run: null
            }]).map(source => (
              <div key={source.source} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16, boxShadow: T.shadow }}>
                <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>{source.source}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{source.total_runs}</div>
                <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>
                  {source.successful_runs} ok · {source.failed_runs} failed
                </div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 8 }}>
                  Last run: {source.last_run || "—"}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, background: T.offWhite }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Recent Runs
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: T.offWhite }}>
                    {["Source", "Status", "Fetched", "Inserted", "Errors", "Duration", "Started"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scraperRuns.map(run => (
                    <tr key={run.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{run.source}</td>
                      <td style={{ padding: "10px 14px", color: run.status === "completed" ? T.success : run.status === "failed" ? T.danger : T.textMid, fontWeight: 600 }}>
                        {run.status}
                      </td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{run.records_fetched}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{run.records_inserted}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{run.records_errors}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{run.duration_seconds ?? "—"}</td>
                      <td style={{ padding: "10px 14px", color: T.textLight, whiteSpace: "nowrap" }}>{run.started_at || "—"}</td>
                    </tr>
                  ))}
                  {scraperRuns.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 20, textAlign: "center", color: T.textLight }}>
                        No scraper runs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, background: T.offWhite }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Recent Errors
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: T.offWhite }}>
                    {["Source", "Type", "Entity", "Message", "When"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scraperErrors.map(err => (
                    <tr key={err.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{err.source}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{err.error_type}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid }}>{err.entity_key || "—"}</td>
                      <td style={{ padding: "10px 14px", color: T.textMid, maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {err.error_message}
                      </td>
                      <td style={{ padding: "10px 14px", color: T.textLight, whiteSpace: "nowrap" }}>{err.created_at || "—"}</td>
                    </tr>
                  ))}
                  {scraperErrors.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: 20, textAlign: "center", color: T.textLight }}>
                        No scraper errors logged.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
